import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Shield, FileText, Lock, CheckCircle2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'

interface Organization {
  id: string
  name: string
}

interface UserProfile {
  id: string
  full_name?: string
}

interface PoliciesPageProps {
  organization: Organization | null
  profile: UserProfile | null
}

export function PoliciesPage({ organization, profile }: PoliciesPageProps) {
  const { t } = useTranslation('common')
  return (
    <div className="space-y-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="space-y-2">
        <h1 className="text-3xl font-bold tracking-tight">{t('policies.title')}</h1>
        <p className="text-muted-foreground leading-relaxed">
          {t('policies.intro')}
        </p>
      </div>

      {/* Policies Tabs */}
      <Card>
        <CardContent className="p-0">
          <Tabs defaultValue="gdpr" className="w-full">
            {/* Mobile: Horizontal tabs */}
            <div className="md:hidden">
              <TabsList className="grid w-full grid-cols-2 h-auto">
                <TabsTrigger value="gdpr" className="flex flex-col gap-1 p-3 text-xs">
                  <Shield className="h-4 w-4" />
                  <span>GDPR</span>
                </TabsTrigger>
                <TabsTrigger value="data" className="flex flex-col gap-1 p-3 text-xs">
                  <FileText className="h-4 w-4" />
                  <span>Data</span>
                </TabsTrigger>
              </TabsList>
              <TabsList className="grid w-full grid-cols-2 h-auto mt-1">
                <TabsTrigger value="security" className="flex flex-col gap-1 p-3 text-xs">
                  <Lock className="h-4 w-4" />
                  <span>Säkerhet</span>
                </TabsTrigger>
                <TabsTrigger value="compliance" className="flex flex-col gap-1 p-3 text-xs">
                  <CheckCircle2 className="h-4 w-4" />
                  <span>Efterlevnad</span>
                </TabsTrigger>
              </TabsList>
            </div>

            {/* Desktop: Vertical tabs */}
            <div className="hidden md:flex">
              <TabsList className="flex flex-col h-auto w-64 p-2">
                <TabsTrigger value="gdpr" className="w-full justify-start gap-3 p-3">
                  <Shield className="h-4 w-4" />
                  {t('policies.gdpr')}
                </TabsTrigger>
                <TabsTrigger value="data" className="w-full justify-start gap-3 p-3">
                  <FileText className="h-4 w-4" />
                  {t('policies.datapolicy')}
                </TabsTrigger>
                <TabsTrigger value="security" className="w-full justify-start gap-3 p-3">
                  <Lock className="h-4 w-4" />
                  {t('policies.security')}
                </TabsTrigger>
                <TabsTrigger value="compliance" className="w-full justify-start gap-3 p-3">
                  <CheckCircle2 className="h-4 w-4" />
                  {t('policies.compliance')}
                </TabsTrigger>
              </TabsList>
              
              <div className="flex-1 min-h-0">
                <TabsContent value="gdpr" className="mt-0 h-full">
                  <div className="p-6 space-y-4">
                    <div className="flex items-center gap-3 mb-4">
                      <Shield className="h-6 w-6 text-primary" />
                      <h2 className="text-2xl font-semibold">{t('policies.gdpr')}</h2>
                    </div>
                    <div className="prose prose-slate max-w-none space-y-4">
                      <p className="text-muted-foreground leading-relaxed">
                        Vi följer GDPR fullt ut. All data lagras och bearbetas inom EU/EES. Vi samlar endast in och använder personuppgifter som är nödvändiga för att tillhandahålla tjänsten.
                      </p>
                      <p className="text-muted-foreground leading-relaxed">
                        Alla användare har rätt att få tillgång till, korrigera eller radera sina uppgifter. Kontakta oss om du vill utnyttja dessa rättigheter.
                      </p>
                      <p className="text-muted-foreground leading-relaxed">
                        Ingen data delas eller säljs till tredje part.
                      </p>
                      <div className="mt-6 p-4 bg-muted rounded-lg">
                        <p className="text-sm font-medium text-foreground">
                          Dataskyddsombud: Oscar Hermansson, AuditAI AB
                        </p>
                      </div>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="data" className="mt-0 h-full">
                  <div className="p-6 space-y-4">
                    <div className="flex items-center gap-3 mb-4">
                      <FileText className="h-6 w-6 text-primary" />
                      <h2 className="text-2xl font-semibold">Datapolicy</h2>
                    </div>
                    <div className="prose prose-slate max-w-none space-y-4">
                      <p className="text-muted-foreground leading-relaxed">
                        All kunddata lagras på servrar placerade i Sverige och inom EU.
                      </p>
                      <p className="text-muted-foreground leading-relaxed">
                        Analyser sker via Microsoft Azure med garanterad EU-databehandling. Ingen data lämnar EU.
                      </p>
                      <p className="text-muted-foreground leading-relaxed">
                        Vi använder metadata för att förbättra tjänstens prestanda men delar aldrig kundspecifik information.
                      </p>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="security" className="mt-0 h-full">
                  <div className="p-6 space-y-4">
                    <div className="flex items-center gap-3 mb-4">
                      <Lock className="h-6 w-6 text-primary" />
                      <h2 className="text-2xl font-semibold">Säkerhet & Robusthet</h2>
                    </div>
                    <div className="prose prose-slate max-w-none space-y-4">
                      <div>
                        <h4 className="font-semibold text-foreground mb-2">Databasskydd:</h4>
                        <p className="text-muted-foreground leading-relaxed">
                          Kryptering at rest (AES-256) och i transit (TLS 1.2+).
                        </p>
                      </div>
                      <div>
                        <h4 className="font-semibold text-foreground mb-2">Åtkomstkontroll:</h4>
                        <p className="text-muted-foreground leading-relaxed">
                          Rollbaserad accesskontroll (RLS) för att förhindra obehörig åtkomst.
                        </p>
                      </div>
                      <div>
                        <h4 className="font-semibold text-foreground mb-2">Kodkvalitet:</h4>
                        <p className="text-muted-foreground leading-relaxed">
                          All kod blockeras från att gå live om testerna inte passerar, vilket försäkrar robusthet.
                        </p>
                      </div>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="compliance" className="mt-0 h-full">
                  <div className="p-6 space-y-4">
                    <div className="flex items-center gap-3 mb-4">
                      <CheckCircle2 className="h-6 w-6 text-primary" />
                      <h2 className="text-2xl font-semibold">Revision & Efterlevnad</h2>
                    </div>
                    <div className="prose prose-slate max-w-none space-y-4">
                      <p className="text-muted-foreground leading-relaxed">
                        Vi arbetar efter principen Security by Design och följer vedertagna revisionsprinciper.
                      </p>
                      <p className="text-muted-foreground leading-relaxed">
                        Vi dokumenterar löpande alla analyser och kontroller. Interna loggar och processer granskas regelbundet.
                      </p>
                      <p className="text-muted-foreground leading-relaxed">
                        Vi följer ISO 27001-inspirerade rutiner för säkerhet och compliance.
                      </p>
                    </div>
                  </div>
                </TabsContent>
              </div>
            </div>

            {/* Mobile content */}
            <div className="md:hidden">
              <TabsContent value="gdpr" className="p-6 space-y-4">
                <div className="flex items-center gap-3 mb-4">
                  <Shield className="h-6 w-6 text-primary" />
                  <h2 className="text-xl font-semibold">GDPR & Dataskydd</h2>
                </div>
                <div className="space-y-4">
                  <p className="text-muted-foreground leading-relaxed text-sm">
                    Vi följer GDPR fullt ut. All data lagras och bearbetas inom EU/EES. Vi samlar endast in och använder personuppgifter som är nödvändiga för att tillhandahålla tjänsten.
                  </p>
                  <p className="text-muted-foreground leading-relaxed text-sm">
                    Alla användare har rätt att få tillgång till, korrigera eller radera sina uppgifter. Kontakta oss om du vill utnyttja dessa rättigheter.
                  </p>
                  <p className="text-muted-foreground leading-relaxed text-sm">
                    Ingen data delas eller säljs till tredje part.
                  </p>
                  <div className="p-3 bg-muted rounded-lg">
                    <p className="text-sm font-medium">
                      Dataskyddsombud: Oscar Hermansson, AuditAI AB
                    </p>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="data" className="p-6 space-y-4">
                <div className="flex items-center gap-3 mb-4">
                  <FileText className="h-6 w-6 text-primary" />
                  <h2 className="text-xl font-semibold">Datapolicy</h2>
                </div>
                <div className="space-y-4">
                  <p className="text-muted-foreground leading-relaxed text-sm">
                    All kunddata lagras på servrar placerade i Sverige och inom EU.
                  </p>
                  <p className="text-muted-foreground leading-relaxed text-sm">
                    Analyser sker via Microsoft Azure med garanterad EU-databehandling. Ingen data lämnar EU.
                  </p>
                  <p className="text-muted-foreground leading-relaxed text-sm">
                    Vi använder metadata för att förbättra tjänstens prestanda men delar aldrig kundspecifik information.
                  </p>
                </div>
              </TabsContent>

              <TabsContent value="security" className="p-6 space-y-4">
                <div className="flex items-center gap-3 mb-4">
                  <Lock className="h-6 w-6 text-primary" />
                  <h2 className="text-xl font-semibold">Säkerhet & Robusthet</h2>
                </div>
                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold mb-2 text-sm">Databasskydd:</h4>
                    <p className="text-muted-foreground leading-relaxed text-sm">
                      Kryptering at rest (AES-256) och i transit (TLS 1.2+).
                    </p>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-2 text-sm">Åtkomstkontroll:</h4>
                    <p className="text-muted-foreground leading-relaxed text-sm">
                      Rollbaserad accesskontroll (RLS) för att förhindra obehörig åtkomst.
                    </p>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-2 text-sm">Kodkvalitet:</h4>
                    <p className="text-muted-foreground leading-relaxed text-sm">
                      All kod blockeras från att gå live om testerna inte passerar, vilket försäkrar robusthet.
                    </p>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="compliance" className="p-6 space-y-4">
                <div className="flex items-center gap-3 mb-4">
                  <CheckCircle2 className="h-6 w-6 text-primary" />
                  <h2 className="text-xl font-semibold">Revision & Efterlevnad</h2>
                </div>
                <div className="space-y-4">
                  <p className="text-muted-foreground leading-relaxed text-sm">
                    Vi arbetar efter principen Security by Design och följer vedertagna revisionsprinciper.
                  </p>
                  <p className="text-muted-foreground leading-relaxed text-sm">
                    Vi dokumenterar löpande alla analyser och kontroller. Interna loggar och processer granskas regelbundet.
                  </p>
                  <p className="text-muted-foreground leading-relaxed text-sm">
                    Vi följer ISO 27001-inspirerade rutiner för säkerhet och compliance.
                  </p>
                </div>
              </TabsContent>
            </div>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  )
}